import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import clsx from "clsx";
import { RefreshCw, ImageOff } from "lucide-react";

import { Button } from "../../components/common/Button";
import { Input } from "../../components/common/Input";
import { Select } from "../../components/common/Select";
import { Spinner } from "../../components/common/Spinner";
import { ConfirmDialog } from "../../components/common/ConfirmDialog";
import { getChannelSnapshot, getChannelTitles, getMotionDetect, patchMotionDetect } from "../../api/config";
import type { Device } from "../../types";

const ROWS_VISIBLE = 18;
const ROWS_TOTAL = 32;
const COLS = 22;
const FULL_ROW_MASK = (1 << COLS) - 1;
const REGION_COUNT = 4;

const REGION_COLORS = [
  { name: "Roja", chip: "bg-red-500", cell: "bg-red-500/40", cellHover: "hover:bg-red-500/60", border: "border-red-400", text: "text-red-200" },
  { name: "Amarilla", chip: "bg-yellow-400", cell: "bg-yellow-400/40", cellHover: "hover:bg-yellow-400/60", border: "border-yellow-300", text: "text-yellow-100" },
  { name: "Azul", chip: "bg-sky-500", cell: "bg-sky-500/40", cellHover: "hover:bg-sky-500/60", border: "border-sky-400", text: "text-sky-200" },
  { name: "Verde", chip: "bg-emerald-500", cell: "bg-emerald-500/40", cellHover: "hover:bg-emerald-500/60", border: "border-emerald-400", text: "text-emerald-200" },
];

interface EventHandler {
  Dejitter?: number;
  Delay?: number;
  LogEnable?: boolean;
  MailEnable?: boolean;
  BeepEnable?: boolean;
  MessageEnable?: boolean;
  MessageToNetEnable?: boolean;
  MMSEnable?: boolean;
  AlarmOutEnable?: boolean;
  AlarmOutLatch?: number;
  RecordEnable?: boolean;
  RecordLatch?: number;
  SnapshotEnable?: boolean;
  SnapshotTimes?: number;
  SnapshotPeriod?: number;
  PtzLinkEnable?: boolean;
  EventHttpUploadEnable?: boolean;
  TriggerHttpInfo?: {
    TriggerEnable?: boolean;
    RequestUrl?: string;
    AuthEnable?: boolean;
    [k: string]: unknown;
  };
  [k: string]: unknown;
}

interface RegionWindow {
  Id: number;
  Name: string;
  Region: number[];
  Sensitive: number;
  Threshold: number;
}

interface MotionChannel {
  Enable?: boolean;
  DetectVersion?: string;
  MotionDetectWindow?: Array<{
    Id?: number;
    Name?: string;
    Region?: number[];
    Sensitive?: number;
    Threshold?: number;
    [k: string]: unknown;
  }>;
  EventHandler?: EventHandler;
  [k: string]: unknown;
}

function emptyMask(): number[] {
  return Array.from({ length: ROWS_TOTAL }, () => 0);
}

function fullMaskVisible(): number[] {
  return Array.from({ length: ROWS_TOTAL }, (_, i) => i < ROWS_VISIBLE ? FULL_ROW_MASK : 0);
}

// Dahua bit endianness: bit 0 = columna derecha, bit (COLS-1) = columna izquierda.
// Verificado empiricamente: pintar la izquierda en la UI y leer en el NVR mostraba
// invertido si usabamos bit 0 = columna izquierda. La columna visible 0 (izquierda)
// se mapea al bit (COLS-1-0) = 21 del int.
function bitForCol(col: number): number {
  return COLS - 1 - col;
}

function isCellSet(mask: number[], row: number, col: number): boolean {
  const v = mask[row] ?? 0;
  return ((v >> bitForCol(col)) & 1) === 1;
}

function setCell(mask: number[], row: number, col: number, on: boolean): number[] {
  const next = [...mask];
  const cur = next[row] ?? 0;
  const b = bitForCol(col);
  next[row] = on ? cur | (1 << b) : cur & ~(1 << b);
  return next;
}

function normalizeRegions(raw: MotionChannel["MotionDetectWindow"]): RegionWindow[] {
  const out: RegionWindow[] = [];
  for (let i = 0; i < REGION_COUNT; i++) {
    const src = raw?.[i] || {};
    const region = Array.from({ length: ROWS_TOTAL }, (_, r) => {
      const v = (src.Region as number[] | undefined)?.[r];
      return typeof v === "number" ? v : 0;
    });
    out.push({
      Id: typeof src.Id === "number" ? src.Id : i,
      Name: typeof src.Name === "string" && src.Name ? src.Name : `Region${i + 1}`,
      Region: region,
      Sensitive: typeof src.Sensitive === "number" ? src.Sensitive : (i === 0 ? 90 : 60),
      Threshold: typeof src.Threshold === "number" ? src.Threshold : (i === 0 ? 15 : 5),
    });
  }
  return out;
}

export function MotionDetectTab({ deviceId, device }: { deviceId: number; device: Device }) {
  const [full, setFull] = useState<{ MotionDetect?: MotionChannel[] } | null>(null);
  const [channelIdx, setChannelIdx] = useState(0);
  const [enabled, setEnabled] = useState(true);
  const [dejitter, setDejitter] = useState(10);
  const [handler, setHandler] = useState<EventHandler>({});
  const [regions, setRegions] = useState<RegionWindow[]>(() => normalizeRegions(undefined));
  const [activeRegion, setActiveRegion] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [paintMode, setPaintMode] = useState<"add" | "remove" | null>(null);
  const [applyAllOpen, setApplyAllOpen] = useState(false);
  const [channelNames, setChannelNames] = useState<string[]>([]);
  const [snapshotUrl, setSnapshotUrl] = useState<string | null>(null);
  const [snapshotState, setSnapshotState] = useState<"idle" | "loading" | "ok" | "error">("idle");

  const reload = async () => {
    setLoading(true);
    try {
      const d = await getMotionDetect(deviceId);
      setFull(d);
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Error leyendo Motion Detect");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    getChannelTitles(deviceId)
      .then((d) => setChannelNames(d.titles.map((t) => t.name)))
      .catch(() => setChannelNames([]));
  }, [deviceId]);

  const loadSnapshot = async () => {
    setSnapshotState("loading");
    try {
      const blob = await getChannelSnapshot(deviceId, channelIdx + 1);
      const url = URL.createObjectURL(blob);
      setSnapshotUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return url; });
      setSnapshotState("ok");
    } catch {
      setSnapshotState("error");
      setSnapshotUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
    }
  };

  useEffect(() => {
    loadSnapshot();
    return () => {
      setSnapshotUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId, channelIdx]);

  useEffect(() => {
    const ch = full?.MotionDetect?.[channelIdx];
    if (!ch) return;
    setEnabled(!!ch.Enable);
    const h = ch.EventHandler || {};
    setHandler(JSON.parse(JSON.stringify(h)));
    setDejitter(typeof h.Dejitter === "number" ? h.Dejitter : 10);
    setRegions(normalizeRegions(ch.MotionDetectWindow));
    setActiveRegion(0);
  }, [full, channelIdx]);

  const totalChannels = full?.MotionDetect?.length || device.channels_count || 1;
  const channelOptions = useMemo(
    () => Array.from({ length: totalChannels }, (_, i) => ({
      value: String(i),
      label: channelNames[i] ? `CH${i + 1} - ${channelNames[i]}` : `CH${i + 1}`,
    })),
    [totalChannels, channelNames],
  );

  if (loading || !full) return <Spinner />;

  const active = regions[activeRegion];

  const updateActiveRegion = (patch: Partial<RegionWindow>) => {
    setRegions((rs) => rs.map((r, i) => i === activeRegion ? { ...r, ...patch } : r));
  };

  const updateHandler = (patch: Partial<EventHandler>) => {
    setHandler((h) => ({ ...h, ...patch }));
  };

  const paintCell = (row: number, col: number) => {
    if (paintMode === null) return;
    updateActiveRegion({ Region: setCell(active.Region, row, col, paintMode === "add") });
  };

  const handleMouseDownCell = (row: number, col: number) => {
    const on = !isCellSet(active.Region, row, col);
    setPaintMode(on ? "add" : "remove");
    updateActiveRegion({ Region: setCell(active.Region, row, col, on) });
  };

  const fillActive = () => updateActiveRegion({ Region: fullMaskVisible() });
  const clearActive = () => updateActiveRegion({ Region: emptyMask() });

  const buildMainPayload = (idx: number): Record<string, string> => {
    const p = `MotionDetect[${idx}]`;
    const eh = `${p}.EventHandler`;
    const h = handler;
    const t = h.TriggerHttpInfo || {};
    return {
      [`${p}.Enable`]: enabled ? "true" : "false",
      [`${eh}.Dejitter`]: String(dejitter),
      [`${eh}.RecordEnable`]: h.RecordEnable ? "true" : "false",
      [`${eh}.RecordLatch`]: String(h.RecordLatch ?? 10),
      [`${eh}.SnapshotEnable`]: h.SnapshotEnable ? "true" : "false",
      [`${eh}.SnapshotTimes`]: String(h.SnapshotTimes ?? 1),
      [`${eh}.SnapshotPeriod`]: String(h.SnapshotPeriod ?? 1),
      [`${eh}.AlarmOutEnable`]: h.AlarmOutEnable ? "true" : "false",
      [`${eh}.AlarmOutLatch`]: String(h.AlarmOutLatch ?? 10),
      [`${eh}.LogEnable`]: h.LogEnable ? "true" : "false",
      [`${eh}.MailEnable`]: h.MailEnable ? "true" : "false",
      [`${eh}.BeepEnable`]: h.BeepEnable ? "true" : "false",
      [`${eh}.MessageEnable`]: h.MessageEnable ? "true" : "false",
      [`${eh}.MessageToNetEnable`]: h.MessageToNetEnable ? "true" : "false",
      [`${eh}.MMSEnable`]: h.MMSEnable ? "true" : "false",
      [`${eh}.EventHttpUploadEnable`]: h.EventHttpUploadEnable ? "true" : "false",
      [`${eh}.PtzLinkEnable`]: h.PtzLinkEnable ? "true" : "false",
      [`${eh}.TriggerHttpInfo.TriggerEnable`]: t.TriggerEnable ? "true" : "false",
      [`${eh}.TriggerHttpInfo.RequestUrl`]: String(t.RequestUrl ?? ""),
    };
  };

  const buildRegionPayload = (idx: number, ri: number): Record<string, string> => {
    const w = `MotionDetect[${idx}].MotionDetectWindow[${ri}]`;
    const r = regions[ri];
    const out: Record<string, string> = {
      [`${w}.Id`]: String(r.Id),
      [`${w}.Name`]: r.Name,
      [`${w}.Sensitive`]: String(r.Sensitive),
      [`${w}.Threshold`]: String(r.Threshold),
    };
    r.Region.forEach((v, i) => {
      out[`${w}.Region[${i}]`] = String(v);
    });
    return out;
  };

  const saveChannel = async (idx: number): Promise<boolean> => {
    try {
      await patchMotionDetect(deviceId, buildMainPayload(idx));
      for (let ri = 0; ri < REGION_COUNT; ri++) {
        await patchMotionDetect(deviceId, buildRegionPayload(idx, ri));
      }
      return true;
    } catch {
      return false;
    }
  };

  const save = async () => {
    setSaving(true);
    const ok = await saveChannel(channelIdx);
    setSaving(false);
    if (ok) {
      toast.success(`Motion Detect CH${channelIdx + 1} actualizado`);
      await reload();
    } else {
      toast.error("Fallo al guardar Motion Detect");
    }
  };

  const applyToAll = async () => {
    setApplyAllOpen(false);
    setSaving(true);
    const errors: number[] = [];
    for (let i = 0; i < totalChannels; i++) {
      const ok = await saveChannel(i);
      if (!ok) errors.push(i + 1);
    }
    setSaving(false);
    if (errors.length === 0) {
      toast.success(`Aplicado a ${totalChannels} canales`);
    } else if (errors.length < totalChannels) {
      toast.error(`Aplicado a ${totalChannels - errors.length}/${totalChannels} canales. Fallaron: ${errors.join(", ")}`);
    } else {
      toast.error("Fallo aplicando a todos los canales");
    }
    await reload();
  };

  const cellRegionIndex = (row: number, col: number): number => {
    if (activeRegion >= 0 && isCellSet(regions[activeRegion].Region, row, col)) return activeRegion;
    for (let i = 0; i < REGION_COUNT; i++) {
      if (i === activeRegion) continue;
      if (isCellSet(regions[i].Region, row, col)) return i;
    }
    return -1;
  };

  return (
    <div className="space-y-4 max-w-5xl" onMouseUp={() => setPaintMode(null)} onMouseLeave={() => setPaintMode(null)}>
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-base font-semibold">Detección de movimiento</h2>
          <p className="text-xs text-muted mt-0.5">
            Hasta 4 regiones independientes por canal con sensibilidad y umbral propios.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <Select
            label="Canal"
            options={channelOptions}
            value={String(channelIdx)}
            onChange={(e) => setChannelIdx(Number(e.target.value))}
            className="min-w-[200px]"
          />
          <Button variant="secondary" onClick={() => setApplyAllOpen(true)} disabled={saving}>
            Aplicar a todos los canales
          </Button>
        </div>
      </header>

      <section className="border border-line rounded p-3 bg-panel/30 flex items-center justify-between flex-wrap gap-3">
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          <span>Detección habilitada</span>
        </label>
        <Input
          label="Anti-dither (s)"
          type="number"
          min={0}
          max={600}
          value={String(dejitter)}
          onChange={(e) => setDejitter(Number(e.target.value))}
          className="w-28"
        />
      </section>

      <section className="border border-line rounded p-3 bg-panel/30 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold">Región:</span>
            {regions.map((r, i) => {
              const c = REGION_COLORS[i];
              const isActive = i === activeRegion;
              return (
                <button
                  key={i}
                  onClick={() => setActiveRegion(i)}
                  title={r.Name}
                  className={clsx(
                    "w-7 h-7 rounded border-2 transition-all",
                    c.chip,
                    isActive ? "border-white scale-110 shadow-md" : "border-transparent opacity-70 hover:opacity-100",
                  )}
                />
              );
            })}
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" onClick={loadSnapshot} disabled={snapshotState === "loading"}>
              <RefreshCw size={12} className={clsx(snapshotState === "loading" && "animate-spin")} />
              Actualizar imagen
            </Button>
            <Button size="sm" variant="ghost" onClick={fillActive}>Seleccionar todo</Button>
            <Button size="sm" variant="ghost" onClick={clearActive}>Limpiar región</Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Input
            label="Nombre de región"
            value={active.Name}
            onChange={(e) => updateActiveRegion({ Name: e.target.value })}
          />
          <div className="flex flex-col gap-1 text-xs">
            <span className="text-muted">Sensibilidad (0-100): <b className={REGION_COLORS[activeRegion].text}>{active.Sensitive}</b></span>
            <input
              type="range" min={0} max={100} value={active.Sensitive}
              onChange={(e) => updateActiveRegion({ Sensitive: Number(e.target.value) })}
              className="accent-accent"
            />
          </div>
          <div className="flex flex-col gap-1 text-xs">
            <span className="text-muted">Umbral (0-100): <b className={REGION_COLORS[activeRegion].text}>{active.Threshold}</b></span>
            <input
              type="range" min={0} max={100} value={active.Threshold}
              onChange={(e) => updateActiveRegion({ Threshold: Number(e.target.value) })}
              className="accent-accent"
            />
          </div>
        </div>

        <p className="text-xs text-muted">
          Click y arrastrar sobre la imagen para pintar/borrar bloques en la <b className={REGION_COLORS[activeRegion].text}>región {REGION_COLORS[activeRegion].name.toLowerCase()}</b>.
        </p>

        <div
          className="relative w-full max-w-[800px] bg-black/60 border border-line overflow-hidden select-none"
          style={{ aspectRatio: `${COLS} / ${ROWS_VISIBLE}` }}
        >
          {snapshotUrl ? (
            <img src={snapshotUrl} alt={`Snapshot CH${channelIdx + 1}`} className="absolute inset-0 w-full h-full object-fill" draggable={false} />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-muted text-xs gap-2">
              {snapshotState === "loading" && <><RefreshCw size={14} className="animate-spin" /> Cargando imagen…</>}
              {snapshotState === "error" && <><ImageOff size={14} /> Sin imagen disponible — la cámara puede estar offline</>}
              {snapshotState === "idle" && "Sin imagen"}
            </div>
          )}
          <div
            className="absolute inset-0 grid"
            style={{ gridTemplateColumns: `repeat(${COLS}, 1fr)`, gridTemplateRows: `repeat(${ROWS_VISIBLE}, 1fr)` }}
          >
            {Array.from({ length: ROWS_VISIBLE }).map((_, row) =>
              Array.from({ length: COLS }).map((_, col) => {
                const ri = cellRegionIndex(row, col);
                const color = ri >= 0 ? REGION_COLORS[ri] : null;
                const isActive = ri === activeRegion;
                return (
                  <div
                    key={`${row}-${col}`}
                    onMouseDown={(e) => { e.preventDefault(); handleMouseDownCell(row, col); }}
                    onMouseEnter={() => paintCell(row, col)}
                    className={clsx(
                      "border cursor-crosshair transition-colors",
                      color ? color.cell : "",
                      color && isActive ? `${color.cellHover} border-white/60` : "border-white/10 hover:bg-white/20",
                    )}
                  />
                );
              }),
            )}
          </div>
        </div>
      </section>

      <section className="border border-line rounded p-3 bg-panel/30 space-y-3">
        <h3 className="text-sm font-semibold">Acciones al detectar movimiento</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={!!handler.RecordEnable} onChange={(e) => updateHandler({ RecordEnable: e.target.checked })} /><span>Grabar video</span></label>
          <Input label="Duración grabación (s)" type="number" min={1} max={600} value={String(handler.RecordLatch ?? 10)} onChange={(e) => updateHandler({ RecordLatch: Number(e.target.value) })} disabled={!handler.RecordEnable} />
          <div />
          <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={!!handler.SnapshotEnable} onChange={(e) => updateHandler({ SnapshotEnable: e.target.checked })} /><span>Tomar foto</span></label>
          <Input label="Cantidad fotos" type="number" min={1} max={20} value={String(handler.SnapshotTimes ?? 1)} onChange={(e) => updateHandler({ SnapshotTimes: Number(e.target.value) })} disabled={!handler.SnapshotEnable} />
          <Input label="Período (s)" type="number" min={1} max={60} value={String(handler.SnapshotPeriod ?? 1)} onChange={(e) => updateHandler({ SnapshotPeriod: Number(e.target.value) })} disabled={!handler.SnapshotEnable} />
          <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={!!handler.AlarmOutEnable} onChange={(e) => updateHandler({ AlarmOutEnable: e.target.checked })} /><span>Activar salida de alarma</span></label>
          <Input label="Duración alarma (s)" type="number" min={1} max={600} value={String(handler.AlarmOutLatch ?? 10)} onChange={(e) => updateHandler({ AlarmOutLatch: Number(e.target.value) })} disabled={!handler.AlarmOutEnable} />
          <div />
          <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={!!handler.LogEnable} onChange={(e) => updateHandler({ LogEnable: e.target.checked })} /><span>Registro en log (Registro)</span></label>
          <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={!!handler.MailEnable} onChange={(e) => updateHandler({ MailEnable: e.target.checked })} /><span>Enviar email (Enviar correo)</span></label>
          <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={!!handler.BeepEnable} onChange={(e) => updateHandler({ BeepEnable: e.target.checked })} /><span>Beep audible (Buzzer)</span></label>
          <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={!!handler.MessageEnable} onChange={(e) => updateHandler({ MessageEnable: e.target.checked })} /><span>Mensaje en monitor (Ver Mensaje)</span></label>
          <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={!!handler.MessageToNetEnable} onChange={(e) => updateHandler({ MessageToNetEnable: e.target.checked })} /><span>Mensaje a clientes (Net Message)</span></label>
          <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={!!handler.MMSEnable} onChange={(e) => updateHandler({ MMSEnable: e.target.checked })} /><span>SMS</span></label>
          <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={!!handler.EventHttpUploadEnable} onChange={(e) => updateHandler({ EventHttpUploadEnable: e.target.checked })} /><span>Alarm Upload (HTTP)</span></label>
          <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={!!handler.PtzLinkEnable} onChange={(e) => updateHandler({ PtzLinkEnable: e.target.checked })} /><span>PTZ link</span></label>
        </div>

        <div className="border-t border-line pt-3 space-y-2">
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={!!handler.TriggerHttpInfo?.TriggerEnable}
              onChange={(e) => updateHandler({ TriggerHttpInfo: { ...(handler.TriggerHttpInfo || {}), TriggerEnable: e.target.checked } })}
            />
            <span>Enviar comando HTTP al detectar movimiento</span>
          </label>
          <Input
            label="URL del comando (GET)"
            placeholder="http://servidor/path?param=valor"
            value={String(handler.TriggerHttpInfo?.RequestUrl ?? "")}
            onChange={(e) => updateHandler({ TriggerHttpInfo: { ...(handler.TriggerHttpInfo || {}), RequestUrl: e.target.value } })}
            disabled={!handler.TriggerHttpInfo?.TriggerEnable}
          />
        </div>
      </section>

      <div className="flex gap-2">
        <Button variant="primary" onClick={save} loading={saving}>Guardar</Button>
        <Button variant="ghost" onClick={reload} disabled={saving}>Restablecer</Button>
      </div>

      <ConfirmDialog
        open={applyAllOpen}
        title="Aplicar Motion Detect a todos los canales"
        message={`Esta accion va a sobrescribir la configuracion de motion detect (las 4 regiones y acciones) en los ${totalChannels} canales. ¿Continuar?`}
        confirmText="Aplicar a todos"
        destructive
        onConfirm={applyToAll}
        onCancel={() => setApplyAllOpen(false)}
      />
    </div>
  );
}
